/**
 * Authentication Service for Poker Reborn
 *
 * Handles multiple auth flows:
 * - Guest login (native only, via Odoo)
 * - Google login (native via @capgo/capacitor-social-login, web via GIS)
 * - Facebook / Apple (scaffold)
 *
 * All credentials stored in unified localStorage key 'poker_credentials'.
 * device_id sent on all auth requests when available (native only, advisory).
 *
 * @see docs/screens/login/README.md - Login flow, auth architecture
 */

import { Capacitor } from '@capacitor/core';
import { EventEmitter } from './EventEmitter';
import { AuthError } from '@/types/auth';
import type { OdooAuthResponse } from '@/types/auth';
import '@/types/apple-auth';
import '@/types/facebook-auth';
import { log } from '@/utils/log';

// =============================================================================
// TYPES
// =============================================================================

/** Auth provider types */
export type AuthProvider = 'guest' | 'google' | 'facebook' | 'apple';

/** Credentials returned from any auth flow */
export interface AuthCredentials {
  username: string;
  password: string;
  provider: AuthProvider;
  /** Display name (from Google profile, or 'Guest' for guests) */
  displayName?: string | undefined;
  /** Profile image URL (from Google) */
  avatarUrl?: string | undefined;
}

/** Auth service events */
export interface AuthServiceEvents {
  /** Authentication successful */
  authenticated: AuthCredentials;
  /** Authentication failed */
  authError: { provider: AuthProvider; error: string };
}

// =============================================================================
// CONSTANTS
// =============================================================================

/** Unified credential storage key — all auth types use this */
const CREDENTIALS_KEY = 'poker_credentials';

// =============================================================================
// AUTH SERVICE
// =============================================================================

export class AuthService extends EventEmitter<AuthServiceEvents> {
  private static instance: AuthService | null = null;
  private socialLoginInitialized = false;

  private constructor() {
    super();
  }

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  // ===========================================================================
  // PUBLIC API — Platform
  // ===========================================================================

  /** Whether running inside a Capacitor native app (iOS/Android) */
  isNativePlatform(): boolean {
    return Capacitor.isNativePlatform();
  }

  /** Get the current platform: 'ios', 'android', or 'web' */
  getPlatform(): 'ios' | 'android' | 'web' {
    return Capacitor.getPlatform() as 'ios' | 'android' | 'web';
  }

  // ===========================================================================
  // PUBLIC API — Credential Storage
  // ===========================================================================

  /** Get stored credentials from localStorage (null if none) */
  getStoredCredentials(): { username: string; password: string } | null {
    const raw = localStorage.getItem(CREDENTIALS_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as { username?: string; password?: string };
      if (parsed.username && parsed.password) {
        return { username: parsed.username, password: parsed.password };
      }
    } catch {
      log.auth.warning`Corrupted poker_credentials in localStorage, clearing`;
      localStorage.removeItem(CREDENTIALS_KEY);
    }
    return null;
  }

  /** Clear stored credentials (used on logout) */
  clearCredentials(): void {
    localStorage.removeItem(CREDENTIALS_KEY);
  }

  /** Check if stored credentials exist */
  hasStoredCredentials(): boolean {
    return this.getStoredCredentials() !== null;
  }

  // ===========================================================================
  // PUBLIC API — Auth Flows
  // ===========================================================================

  /**
   * Guest login — native only, calls Odoo to create account.
   * Reuses stored credentials if they exist.
   */
  async guestLogin(): Promise<AuthCredentials> {
    // Reuse stored credentials if available
    const stored = this.getStoredCredentials();
    if (stored) {
      log.auth.info`Returning with stored credentials: ${stored.username}`;
      const credentials: AuthCredentials = {
        username: stored.username,
        password: stored.password,
        provider: 'guest',
        displayName: 'Guest',
      };
      this.emit('authenticated', credentials);
      return credentials;
    }

    // New guest — need device_id (native only)
    const deviceId = await this.getDeviceId();
    if (!deviceId) {
      throw new AuthError('no_device_id', 'Guest login requires a native device');
    }

    // Create guest via Odoo
    const { odooService } = await import('./OdooService');
    const response = await odooService.createGuest(deviceId);

    if (!response.success || !response.player) {
      throw new AuthError(
        response.error ?? 'unknown',
        response.message ?? 'Guest creation failed',
      );
    }

    // Store credentials
    this.storeCredentials(response.player.username, response.player.password);

    // Store session token if provided
    if (response.session_token) {
      odooService.setSessionToken(response.session_token, response.token_expires_at);
    }

    const credentials: AuthCredentials = {
      username: response.player.username,
      password: response.player.password,
      provider: 'guest',
      displayName: 'Guest',
    };

    this.emit('authenticated', credentials);
    return credentials;
  }

  /**
   * Google login (native) — uses Capacitor SocialLogin plugin.
   */
  async googleLogin(): Promise<AuthCredentials> {
    return this.googleLoginNative();
  }

  /**
   * Google login (web) — receives GIS credential token, exchanges with Odoo.
   * Called by WebLoginScreen after GIS returns a token.
   */
  async googleLoginWeb(googleIdToken: string): Promise<AuthCredentials> {
    try {
      const result = await this.authenticateOrLink('google', googleIdToken);

      const credentials: AuthCredentials = {
        username: result.username,
        password: result.password,
        provider: 'google',
      };

      this.emit('authenticated', credentials);
      return credentials;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log.auth.error`Google web login failed: ${errorMessage}`;
      this.emit('authError', { provider: 'google', error: errorMessage });
      throw error;
    }
  }

  /**
   * Facebook login — native via Capacitor SocialLogin, web via Facebook JS SDK.
   */
  async facebookLogin(): Promise<AuthCredentials> {
    if (this.isNativePlatform()) {
      return this.facebookLoginNative();
    }
    return this.facebookLoginWeb();
  }

  /**
   * Apple login — native via Capacitor SocialLogin, web via Apple JS SDK.
   */
  async appleLogin(): Promise<AuthCredentials> {
    if (this.isNativePlatform()) {
      return this.appleLoginNative();
    }
    return this.appleLoginWeb();
  }

  /**
   * Initialize social login plugins (call once at app startup on native).
   * Safe to call on web — will be a no-op.
   */
  async initializeSocialLogin(): Promise<void> {
    if (!this.isNativePlatform() || this.socialLoginInitialized) return;

    const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
    const googleIosClientId = import.meta.env.VITE_GOOGLE_IOS_CLIENT_ID as string | undefined;
    const platform = this.getPlatform();

    // On iOS, the plugin requires an iOS-specific client ID
    // On Android, the web client ID is sufficient
    const hasGoogleConfig = platform === 'ios'
      ? !!googleIosClientId
      : !!googleClientId;

    if (!hasGoogleConfig) {
      log.auth.info`Social login skipped on ${platform} — no matching OAuth client ID`;
      return;
    }

    try {
      const { SocialLogin } = await import('@capgo/capacitor-social-login');

      const googleConfig: { webClientId?: string; iOSClientId?: string; mode: 'online' } = {
        mode: 'online',
      };
      if (googleClientId) googleConfig.webClientId = googleClientId;
      if (googleIosClientId) googleConfig.iOSClientId = googleIosClientId;

      const facebookAppId = import.meta.env.VITE_FACEBOOK_APP_ID as string | undefined;
      const facebookClientToken = import.meta.env.VITE_FACEBOOK_CLIENT_TOKEN as string | undefined;

      const initOptions: Record<string, unknown> = {
        google: googleConfig,
      };

      if (facebookAppId) {
        initOptions.facebook = {
          appId: facebookAppId,
          clientToken: facebookClientToken ?? '',
        };
      }

      const appleClientId = import.meta.env.VITE_APPLE_CLIENT_ID as string | undefined;
      if (appleClientId) {
        initOptions.apple = {
          clientId: appleClientId,
          redirectUrl: `${window.location.origin}/auth/apple/callback`,
        };
      }

      await SocialLogin.initialize(initOptions);

      this.socialLoginInitialized = true;
      log.auth.info`Social login initialized on ${platform}`;
    } catch (error) {
      // Log but don't re-throw — social login failure is non-fatal
      log.auth.error`Failed to initialize social login: ${error}`;
    }
  }

  // ===========================================================================
  // PRIVATE — Credential Storage
  // ===========================================================================

  storeCredentials(username: string, password: string): void {
    localStorage.setItem(CREDENTIALS_KEY, JSON.stringify({ username, password }));
  }

  // ===========================================================================
  // PRIVATE — Device ID
  // ===========================================================================

  /** Get device identifier (native only, null on web). Advisory metadata.
   *  In dev mode with ?native=true, returns a stable fake device ID for testing. */
  private async getDeviceId(): Promise<string | null> {
    if (this.isNativePlatform()) {
      try {
        const { Device } = await import('@capacitor/device');
        const info = await Device.getId();
        return info.identifier;
      } catch {
        log.auth.warning`Failed to get device_id`;
        return null;
      }
    }

    // Dev shortcut: allow guest login on web with ?native=true
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('native') === 'true') {
      return 'dev-web-device';
    }

    return null;
  }

  // ===========================================================================
  // PRIVATE — Social Auth (shared linking logic)
  // ===========================================================================

  /**
   * Authenticate or link a social provider with Odoo.
   * If stored guest credentials exist → link provider to existing account.
   * Otherwise → fresh social login (new or returning user).
   */
  private async authenticateOrLink(
    provider: string,
    idToken: string,
  ): Promise<{ username: string; password: string }> {
    const deviceId = await this.getDeviceId();
    const stored = this.getStoredCredentials();
    const { odooService } = await import('./OdooService');

    let response: OdooAuthResponse;

    if (stored) {
      // PATH A: Guest account exists — link provider to it
      log.auth.info`Linking ${provider} to existing account ${stored.username}`;
      response = await odooService.socialLink(
        provider, idToken, stored.username, stored.password, deviceId,
      );
    } else {
      // PATH B: No guest — fresh social login
      log.auth.info`Fresh ${provider} login (no existing credentials)`;
      response = await odooService.socialLogin(provider, idToken, deviceId);
    }

    if (!response.success || !response.player) {
      throw new AuthError(
        response.error ?? 'unknown',
        response.message ?? 'Authentication failed',
      );
    }

    // Store credentials in unified localStorage
    this.storeCredentials(response.player.username, response.player.password);

    // Store session token if provided
    if (response.session_token) {
      odooService.setSessionToken(response.session_token, response.token_expires_at);
    }

    return response.player;
  }

  // ===========================================================================
  // PRIVATE — Native Login Methods
  // ===========================================================================

  private async googleLoginNative(): Promise<AuthCredentials> {
    try {
      if (!this.socialLoginInitialized) {
        await this.initializeSocialLogin();
      }

      const { SocialLogin } = await import('@capgo/capacitor-social-login');

      const response = await SocialLogin.login({
        provider: 'google',
        options: {
          scopes: ['email', 'profile'],
        },
      });

      const googleResult = response.result;
      if (googleResult.responseType !== 'online') {
        throw new Error('Expected online response from Google login');
      }

      const idToken = googleResult.idToken;
      if (!idToken) {
        throw new Error('No ID token received from Google');
      }

      log.auth.info`Google native login success: ${googleResult.profile.email ?? 'no email'}`;

      // Exchange token with Odoo (with linking if guest exists)
      const authResponse = await this.authenticateOrLink('google', idToken);

      const credentials: AuthCredentials = {
        username: authResponse.username,
        password: authResponse.password,
        provider: 'google',
        displayName: googleResult.profile.name ?? undefined,
        avatarUrl: googleResult.profile.imageUrl ?? undefined,
      };

      this.emit('authenticated', credentials);
      return credentials;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log.auth.error`Google native login failed: ${errorMessage}`;
      this.emit('authError', { provider: 'google', error: errorMessage });
      throw error;
    }
  }

  private async facebookLoginNative(): Promise<AuthCredentials> {
    try {
      if (!this.socialLoginInitialized) {
        await this.initializeSocialLogin();
      }

      const { SocialLogin } = await import('@capgo/capacitor-social-login');

      const response = await SocialLogin.login({
        provider: 'facebook',
        options: {
          permissions: ['email', 'public_profile'],
        },
      });

      const accessToken = response.result.accessToken?.token;
      if (!accessToken) {
        throw new Error('No access token received from Facebook');
      }

      log.auth.info`Facebook native login success`;

      const authResponse = await this.authenticateOrLink('facebook', accessToken);

      const credentials: AuthCredentials = {
        username: authResponse.username,
        password: authResponse.password,
        provider: 'facebook',
      };

      this.emit('authenticated', credentials);
      return credentials;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log.auth.error`Facebook native login failed: ${errorMessage}`;
      this.emit('authError', { provider: 'facebook', error: errorMessage });
      throw error;
    }
  }

  private async facebookLoginWeb(): Promise<AuthCredentials> {
    try {
      const facebookAppId = import.meta.env.VITE_FACEBOOK_APP_ID as string | undefined;
      if (!facebookAppId) {
        throw new AuthError('config_error', 'Facebook App ID not configured');
      }

      if (typeof window.FB === 'undefined') {
        throw new AuthError('sdk_not_loaded', 'Facebook SDK failed to load. Please refresh the page.');
      }

      // Initialize FB SDK (safe to call multiple times)
      window.FB.init({
        appId: facebookAppId,
        cookie: true,
        xfbml: false,
        version: 'v19.0',
      });

      // Open Facebook login dialog
      const accessToken = await new Promise<string>((resolve, reject) => {
        window.FB!.login((response) => {
          if (response.status === 'connected' && response.authResponse?.accessToken) {
            resolve(response.authResponse.accessToken);
          } else {
            reject(new AuthError('login_cancelled', 'Facebook login was cancelled'));
          }
        }, { scope: 'email,public_profile' });
      });

      log.auth.info`Facebook web login success`;

      const authResponse = await this.authenticateOrLink('facebook', accessToken);

      const credentials: AuthCredentials = {
        username: authResponse.username,
        password: authResponse.password,
        provider: 'facebook',
      };

      this.emit('authenticated', credentials);
      return credentials;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log.auth.error`Facebook web login failed: ${errorMessage}`;
      this.emit('authError', { provider: 'facebook', error: errorMessage });
      throw error;
    }
  }

  private async appleLoginNative(): Promise<AuthCredentials> {
    try {
      if (!this.socialLoginInitialized) {
        await this.initializeSocialLogin();
      }

      const { SocialLogin } = await import('@capgo/capacitor-social-login');

      const response = await SocialLogin.login({
        provider: 'apple',
        options: {
          scopes: ['email', 'name'],
        },
      });

      const idToken = response.result.idToken;
      if (!idToken) {
        throw new Error('No ID token received from Apple');
      }

      log.auth.info`Apple native login success`;

      const authResponse = await this.authenticateOrLink('apple', idToken);

      const credentials: AuthCredentials = {
        username: authResponse.username,
        password: authResponse.password,
        provider: 'apple',
      };

      this.emit('authenticated', credentials);
      return credentials;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log.auth.error`Apple native login failed: ${errorMessage}`;
      this.emit('authError', { provider: 'apple', error: errorMessage });
      throw error;
    }
  }

  private async appleLoginWeb(): Promise<AuthCredentials> {
    try {
      const appleClientId = import.meta.env.VITE_APPLE_CLIENT_ID as string | undefined;
      if (!appleClientId) {
        throw new AuthError('config_error', 'Apple Client ID not configured');
      }

      if (typeof AppleID === 'undefined') {
        throw new AuthError('sdk_not_loaded', 'Apple Sign In SDK failed to load. Please refresh the page.');
      }

      AppleID.auth.init({
        clientId: appleClientId,
        scope: 'name email',
        redirectURI: window.location.origin,
        usePopup: true,
      });

      const response = await AppleID.auth.signIn();
      const idToken = response.authorization.id_token;

      log.auth.info`Apple web login success`;

      const authResponse = await this.authenticateOrLink('apple', idToken);

      const credentials: AuthCredentials = {
        username: authResponse.username,
        password: authResponse.password,
        provider: 'apple',
      };

      this.emit('authenticated', credentials);
      return credentials;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log.auth.error`Apple web login failed: ${errorMessage}`;
      this.emit('authError', { provider: 'apple', error: errorMessage });
      throw error;
    }
  }

}

/** Singleton auth service instance */
export const authService = AuthService.getInstance();
